// ── Shared schedule types ──────────────────────────────────────
import { jphDebugLog } from '@/lib/jphDebug';
import { buildertrend5MethuenSchedule } from '@/lib/buildertrendSeed';

export type ScheduleLinkType = 'FS' | 'SS' | 'FF' | 'SF';

/** Shown when schedule link graph contains a cycle (recalc is skipped). */
export const CIRCULAR_DEPENDENCY_WARNING =
  'Circular dependency detected. Please review task links.';

export interface GanttBarChange {
  item: ScheduleItem;
  mode: 'move' | 'resize-left' | 'resize-right';
  newStart: string;
  newEnd: string;
  newDuration: number;
}

export interface ScheduleItem {
  id: string;
  project_id: string;
  source_template_item_id: string | null;
  task_no: number | null;
  sort_order: number;
  title: string;
  phase: string | null;
  main_stage: string | null;
  construction_sub_stage: string | null;
  duration_days: number;
  start_date: string | null; // ISO date string
  end_date: string | null;
  progress: number;
  is_complete: boolean;
  is_included: boolean;
  assigned_to: string | null;
  assigned_role: string | null;
  notes: string | null;
  manual_date_lock: boolean;
  is_critical: boolean;
  baseline_start_date: string | null;
  baseline_end_date: string | null;
  actual_start_date: string | null;
  actual_end_date: string | null;
  delay_reason: string | null;
  delay_status: 'on_track' | 'at_risk' | 'delayed' | 'completed_late' | 'completed_on_time';
  schedule_risk_level: 'normal' | 'watch' | 'high' | 'critical';
  bar_color: string | null;
  bar_color_mode: 'phase' | 'custom' | null;
  gantt_color: string | null;
  created_at: string;
  updated_at: string;
}

/** Included in Gantt unless explicitly excluded. Null/undefined count as included. */
export function isScheduleItemIncluded(item: { is_included?: boolean | null }): boolean {
  return item.is_included !== false;
}

export function normalizeScheduleItemRow(row: ScheduleItem): ScheduleItem {
  return {
    ...row,
    is_included: row.is_included !== false,
  };
}

/**
 * When every row is explicitly excluded, treat as legacy/corrupt inclusion state for display.
 * Does not write to DB — use handleIncludeAll to persist.
 */
export function recoverAllExcludedScheduleItems(items: ScheduleItem[]): ScheduleItem[] {
  if (items.length === 0) return items;
  if (items.some(isScheduleItemIncluded)) return items;
  console.warn('[scheduleUtils.recoverAllExcludedScheduleItems] all items is_included=false — restoring display defaults', {
    count: items.length,
  });
  return items.map((item) => ({ ...item, is_included: true }));
}

export interface ScheduleLink {
  id: string;
  project_id: string;
  predecessor_item_id: string;
  successor_item_id: string;
  link_type: ScheduleLinkType;
  lag_days: number;
  created_at: string;
}

export interface ScheduleTemplate {
  id: string;
  name: string;
  project_type: string;
  description: string | null;
  is_active: boolean;
  is_default?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ScheduleTemplateItem {
  id: string;
  template_id: string;
  sort_order: number;
  default_task_no: number | null;
  title: string;
  phase: string | null;
  default_duration_days: number;
  default_is_included: boolean;
  default_assigned_role: string | null;
  default_assigned_to: string | null;
  notes: string | null;
}

export interface ScheduleTemplateLink {
  id: string;
  template_id: string;
  predecessor_item_id: string;
  successor_item_id: string;
  link_type: ScheduleLinkType;
  lag_days: number;
}

// ── Schedule Settings ────────────────────────────────────────
export interface ScheduleSettings {
  id: string;
  project_id: string;
  working_days: number[];
  sunday_off: boolean;
  saturday_off: boolean;
  exclude_public_holidays: boolean;
  holiday_country: string;
  holiday_region: string | null;
  created_at: string;
  updated_at: string;
}

export interface HolidayInfo {
  date: string; // ISO date string
  name?: string;
}

// ── Day-of-week helpers ──────────────────────────────────────
export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ── Working day helpers ──────────────────────────────────────

export function toISODate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseISODate(str: string): Date {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function isHoliday(date: Date, holidays: HolidayInfo[]): boolean {
  const iso = toISODate(date);
  return holidays.some((h) => h.date === iso);
}

function isDayWorking(date: Date, settings: ScheduleSettings): boolean {
  const dow = date.getDay();
  if (settings.working_days && settings.working_days.length > 0) {
    return settings.working_days.includes(dow);
  }
  // fallback
  if (settings.sunday_off && dow === 0) return false;
  if (settings.saturday_off && dow === 6) return false;
  return true;
}

export function isWorkingDay(dateStr: string, settings: ScheduleSettings, holidays: HolidayInfo[] = []): boolean {
  const date = parseISODate(dateStr);
  if (!isDayWorking(date, settings)) return false;
  if (settings.exclude_public_holidays && isHoliday(date, holidays)) return false;
  return true;
}

/** Return ISO string YYYY-MM-DD from a Date */
export function formatDateOnly(date: Date): string {
  return toISODate(date);
}

/** Parse YYYY-MM-DD safely without timezone shifting */
export function parseDateOnly(value: string): Date {
  return parseISODate(value);
}

/** Convenience: end_date = shiftToWorkingDay(start) + addWorkingDays(..., duration-1) */
export function calculateEndDate(startDate: string, durationDays: number, settings: ScheduleSettings, holidays: HolidayInfo[] = []): string {
  const start = shiftToWorkingDay(startDate, settings, holidays);
  const dur = durationDays || 1;
  return addWorkingDays(start, dur - 1, settings, holidays);
}

export function isWorkingDaySimple(dateStr: string): boolean {
  const date = parseISODate(dateStr);
  const dow = date.getDay();
  return dow !== 0 && dow !== 6;
}

/**
 * Given a startDate and numberOfWorkingDays, returns the date that is
 * `days` working days AFTER startDate.
 * If days=0, returns startDate if working, else next working day.
 * For a task: end_date = addWorkingDays(start_date, duration_days - 1)
 */
export function addWorkingDays(startDateStr: string, days: number, settings: ScheduleSettings, holidays: HolidayInfo[] = []): string {
  if (days <= 0) return shiftToWorkingDay(startDateStr, settings, holidays);
  const date = parseISODate(startDateStr);
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    if (isWorkingDay(toISODate(date), settings, holidays)) added++;
  }
  return toISODate(date);
}

/**
 * Step backward N working days from a date.
 * Used for backward pass in CPM.
 */
export function subtractWorkingDays(startDateStr: string, days: number, settings: ScheduleSettings, holidays: HolidayInfo[] = []): string {
  if (days <= 0) return shiftToWorkingDay(startDateStr, settings, holidays);
  const date = parseISODate(startDateStr);
  let subtracted = 0;
  while (subtracted < days) {
    date.setDate(date.getDate() - 1);
    if (isWorkingDay(toISODate(date), settings, holidays)) subtracted++;
  }
  return toISODate(date);
}

/**
 * Return the next date strictly after the given date that is a working day.
 */
export function nextWorkingDay(dateStr: string, settings: ScheduleSettings, holidays: HolidayInfo[] = []): string {
  const date = parseISODate(dateStr);
  do {
    date.setDate(date.getDate() + 1);
  } while (!isWorkingDay(toISODate(date), settings, holidays));
  return toISODate(date);
}

/**
 * If the date is a working day, return it. Otherwise move forward.
 */
export function shiftToWorkingDay(dateStr: string, settings: ScheduleSettings, holidays: HolidayInfo[] = []): string {
  const date = parseISODate(dateStr);
  while (!isWorkingDay(toISODate(date), settings, holidays)) {
    date.setDate(date.getDate() + 1);
  }
  return toISODate(date);
}

/**
 * Return the previous date strictly before the given date that is a working day.
 */
export function previousWorkingDay(dateStr: string, settings: ScheduleSettings, holidays: HolidayInfo[] = []): string {
  const date = parseISODate(dateStr);
  do {
    date.setDate(date.getDate() - 1);
  } while (!isWorkingDay(toISODate(date), settings, holidays));
  return toISODate(date);
}

/**
 * Add lagDays working days to baseDate. Supports negative lag (step backward).
 */
export function addLagWorkingDays(baseDateStr: string, lagDays: number, settings: ScheduleSettings, holidays: HolidayInfo[] = []): string {
  if (lagDays === 0) return baseDateStr;
  const date = parseISODate(baseDateStr);
  const step = lagDays > 0 ? 1 : -1;
  let moved = 0;
  const target = Math.abs(lagDays);
  while (moved < target) {
    date.setDate(date.getDate() + step);
    if (isWorkingDay(toISODate(date), settings, holidays)) moved++;
  }
  return toISODate(date);
}

/**
 * Canonical successor start date from a single dependency link.
 *
 * FS: base = next working day strictly after predecessor end; start = base + lag (working days).
 * SS: base = predecessor start; start = base + lag (working days).
 */
export function calculateSuccessorStartDate(
  linkType: ScheduleLinkType,
  predecessorStart: string | null | undefined,
  predecessorEnd: string | null | undefined,
  lagDays: number,
  settings: ScheduleSettings,
  holidays: HolidayInfo[] = []
): string | null {
  if (!predecessorStart || !predecessorEnd) return null;

  if (linkType === 'FS') {
    const fsBaseStart = nextWorkingDay(predecessorEnd, settings, holidays);
    return addLagWorkingDays(fsBaseStart, lagDays, settings, holidays);
  }

  if (linkType === 'SS') {
    const ssBaseStart = shiftToWorkingDay(predecessorStart, settings, holidays);
    return addLagWorkingDays(ssBaseStart, lagDays, settings, holidays);
  }

  return null;
}

/** Self-check for FS/SS lag date rules (no test framework required). */
export function selfCheckScheduleLinkDateCalculation(): boolean {
  const settings: ScheduleSettings = {
    id: 'self-check',
    project_id: 'self-check',
    working_days: [0, 1, 2, 3, 4, 5, 6],
    sunday_off: false,
    saturday_off: false,
    exclude_public_holidays: false,
    holiday_country: 'NZ',
    holiday_region: null,
    created_at: '',
    updated_at: '',
  };
  const predStart = '2026-07-01';
  const predEnd = '2026-07-03';

  const cases: [number, string][] = [
    [0, '2026-07-04'],
    [-1, '2026-07-03'],
    [-2, '2026-07-02'],
    [1, '2026-07-05'],
  ];
  for (const [lag, expected] of cases) {
    const got = calculateSuccessorStartDate('FS', predStart, predEnd, lag, settings);
    if (got !== expected) return false;
  }

  const ssCases: [number, string][] = [
    [0, '2026-07-01'],
    [1, '2026-07-02'],
    [-1, '2026-06-30'],
  ];
  for (const [lag, expected] of ssCases) {
    const got = calculateSuccessorStartDate('SS', predStart, predEnd, lag, settings);
    if (got !== expected) return false;
  }

  // Editing lag 0 -> -1 -> 0 should not drift (same predecessor dates).
  const lag0 = calculateSuccessorStartDate('FS', predStart, predEnd, 0, settings);
  const lagNeg1 = calculateSuccessorStartDate('FS', predStart, predEnd, -1, settings);
  const lag0Again = calculateSuccessorStartDate('FS', predStart, predEnd, 0, settings);
  if (lag0 !== '2026-07-04' || lagNeg1 !== '2026-07-03' || lag0Again !== lag0) return false;

  return true;
}

/**
 * Count working days between two dates (exclusive of start, inclusive of end when positive).
 * Used for display/logging.
 */
export function workingDaysBetween(start: string, end: string, settings: ScheduleSettings, holidays: HolidayInfo[] = []): number {
  let count = 0;
  const d = parseISODate(start);
  const endD = parseISODate(end);
  const step = endD >= d ? 1 : -1;
  const target = toISODate(endD);
  while (toISODate(d) !== target) {
    d.setDate(d.getDate() + step);
    if (isWorkingDay(toISODate(d), settings, holidays)) count += step;
  }
  return count;
}

/**
 * Count working days between two dates inclusive.
 * If end_date is before start_date, return 1.
 * Minimum duration_days is 1.
 */
export function countWorkingDaysInclusive(start: string, end: string, settings: ScheduleSettings, holidays: HolidayInfo[] = []): number {
  const s = parseISODate(start);
  const e = parseISODate(end);
  if (e < s) return 1;
  let count = 0;
  const d = new Date(s);
  const endIso = toISODate(e);
  while (true) {
    const iso = toISODate(d);
    if (isWorkingDay(iso, settings, holidays)) count++;
    if (iso === endIso) break;
    d.setDate(d.getDate() + 1);
  }
  return Math.max(1, count);
}

// ── Recalculation engine ──────────────────────────────────────
export interface RecalculationResult {
  updatedItems: Map<string, { start_date: string; end_date: string; duration_days: number }>;
  affectedCompletedItems: string[];
  circularWarning: string | null;
  totalMoved: number;
  changeDescriptions: string[];
  blockedMoves: string[];
  controllingDependencies?: Map<string, { linkType: string; predecessorId: string; predecessorTitle: string; lagDays: number }>;
}

function calendarDayDelta(from: string | null | undefined, to: string | null | undefined): number {
  if (!from || !to) return 0;
  const a = parseISODate(from);
  const b = parseISODate(to);
  return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
}

/** Calendar-day difference between two ISO dates (exported for cascade delta). */
export function scheduleCalendarDayDelta(from: string | null | undefined, to: string | null | undefined): number {
  return calendarDayDelta(from, to);
}

/**
 * Recalculate schedule dates from dependency links.
 *
 * @param items            all schedule items
 * @param links            all schedule links
 * @param settings         working-day settings
 * @param holidays         public + project holidays
 * @param options
 *   triggerItemIds       – only propagate downstream from these items.
 *                          When empty, a full forward pass is performed.
 *   skipCompletedItems   – if true (default), items that are already
 *                          complete or have actual dates are NOT moved.
 *                          Their current dates still act as anchors for
 *                          successors.
 *   projectId            – for debug logging.
 *   triggerSource        – for debug logging (e.g. 'saveItem', 'upsertLink').
 */
export function recalculateSchedule(
  items: ScheduleItem[],
  links: ScheduleLink[],
  settings: ScheduleSettings,
  holidays: HolidayInfo[] = [],
  options: {
    triggerItemIds?: string[];
    /** Items that keep their current dates — incoming constraints are not applied (manual moves). */
    fixedItemIds?: string[];
    skipCompletedItems?: boolean;
    projectId?: string;
    triggerSource?: string;
    /** Calendar-day delta of the source/trigger move — used to guard excessive cascade jumps. */
    sourceDeltaDays?: number;
    maxExcessMoveDays?: number;
  } = {}
): RecalculationResult {
  const {
    triggerItemIds = [],
    fixedItemIds = [],
    skipCompletedItems = true,
    projectId,
    triggerSource,
    sourceDeltaDays,
    maxExcessMoveDays = 14,
  } = options;

  const fixedIds = new Set(fixedItemIds);
  const baselineSnapshot = new Map(
    items.map((i) => [i.id, { start_date: i.start_date, end_date: i.end_date }])
  );
  const blockedMoves: string[] = [];

  jphDebugLog('Schedule dependency recalculation start', {
    projectId: projectId || 'unknown',
    triggerItemIds,
    triggerSource: triggerSource || 'auto',
    itemCount: items.length,
    linkCount: links.length,
  });
  jphDebugLog('Dependency links', links.map((l) => ({
    id: l.id,
    predId: l.predecessor_item_id,
    succId: l.successor_item_id,
    type: l.link_type,
    lag: l.lag_days,
  })));

  const itemMap = new Map(items.map((i) => [i.id, { ...i }]));
  const updated = new Map<string, { start_date: string; end_date: string; duration_days: number }>();
  const affectedCompleted: string[] = [];
  const changeDescriptions: string[] = [];
  let circularWarning: string | null = null;

  // ── Cycle detection ──
  const visited = new Set<string>();
  const stack = new Set<string>();
  function detectCycle(nodeId: string): boolean {
    if (stack.has(nodeId)) return true;
    if (visited.has(nodeId)) return false;
    visited.add(nodeId);
    stack.add(nodeId);
    const successors = links
      .filter((l) => l.predecessor_item_id === nodeId)
      .map((l) => l.successor_item_id);
    for (const s of successors) {
      if (detectCycle(s)) return true;
    }
    stack.delete(nodeId);
    return false;
  }
  for (const item of items) {
    if (detectCycle(item.id)) {
      circularWarning = CIRCULAR_DEPENDENCY_WARNING;
      break;
    }
  }
  if (circularWarning) {
    return { updatedItems: updated, affectedCompletedItems: affectedCompleted, circularWarning, totalMoved: 0, changeDescriptions: [], blockedMoves: [] };
  }

  // ── Helper: end date from an item (computed if missing) ──
  function getEndDate(item: ScheduleItem): string | null {
    if (item.end_date) return item.end_date;
    if (item.start_date && item.duration_days > 0) {
      return addWorkingDays(item.start_date, item.duration_days - 1, settings, holidays);
    }
    return item.start_date;
  }

  // ── Helper: constraint from a single link ──
  function computeLinkConstraint(
    pred: ScheduleItem,
    succ: ScheduleItem,
    link: ScheduleLink
  ): { constrainedStart: string | null; constrainedEnd: string | null } {
    const predStart = pred.start_date;
    const predEnd = getEndDate(pred);
    if (!predStart || !predEnd) return { constrainedStart: null, constrainedEnd: null };

    if (link.link_type === 'FS') {
      const succStart = calculateSuccessorStartDate('FS', predStart, predEnd, link.lag_days, settings, holidays);
      if (!succStart) return { constrainedStart: null, constrainedEnd: null };
      const succEnd = addWorkingDays(succStart, Math.max(1, succ.duration_days) - 1, settings, holidays);
      return { constrainedStart: succStart, constrainedEnd: succEnd };
    }

    if (link.link_type === 'SS') {
      const succStart = calculateSuccessorStartDate('SS', predStart, predEnd, link.lag_days, settings, holidays);
      if (!succStart) return { constrainedStart: null, constrainedEnd: null };
      const succEnd = addWorkingDays(succStart, Math.max(1, succ.duration_days) - 1, settings, holidays);
      return { constrainedStart: succStart, constrainedEnd: succEnd };
    }

    if (link.link_type === 'FF') {
      const targetEnd = addLagWorkingDays(predEnd, link.lag_days, settings, holidays);
      const validEnd = shiftToWorkingDay(targetEnd, settings, holidays);
      const dur = Math.max(1, succ.duration_days);
      const succStart = subtractWorkingDays(validEnd, dur - 1, settings, holidays);
      return { constrainedStart: succStart, constrainedEnd: validEnd };
    }

    if (link.link_type === 'SF') {
      const targetEnd = addLagWorkingDays(predStart, link.lag_days, settings, holidays);
      const validEnd = shiftToWorkingDay(targetEnd, settings, holidays);
      const dur = Math.max(1, succ.duration_days);
      const succStart = subtractWorkingDays(validEnd, dur - 1, settings, holidays);
      return { constrainedStart: succStart, constrainedEnd: validEnd };
    }

    return { constrainedStart: null, constrainedEnd: null };
  }

  // ── Build adjacency (predecessor → successors) ──
  const adj = new Map<string, string[]>();
  for (const item of items) {
    adj.set(item.id, []);
  }
  for (const link of links) {
    if (adj.has(link.predecessor_item_id)) {
      adj.get(link.predecessor_item_id)!.push(link.successor_item_id);
    }
  }

  // ── Determine the set of items we are allowed to move ──
  let downstreamIds: Set<string>;
  if (triggerItemIds.length > 0) {
    downstreamIds = new Set<string>(triggerItemIds); // Include triggers themselves
    const queue = [...triggerItemIds];
    while (queue.length) {
      const id = queue.shift()!;
      for (const succ of adj.get(id) || []) {
        if (!downstreamIds.has(succ)) {
          downstreamIds.add(succ);
          queue.push(succ);
        }
      }
    }
  } else {
    downstreamIds = new Set(items.filter((i) => i.is_included).map((i) => i.id));
  }

  jphDebugLog('Affected downstream items', Array.from(downstreamIds));

  // ── Topological sort of ALL items so constraints resolve in correct order ──
  const inDegree = new Map<string, number>();
  for (const item of items) inDegree.set(item.id, 0);
  for (const link of links) {
    if (inDegree.has(link.successor_item_id)) {
      inDegree.set(link.successor_item_id, (inDegree.get(link.successor_item_id) || 0) + 1);
    }
  }

  const queue = Array.from(inDegree.entries())
    .filter(([, v]) => v === 0)
    .map(([k]) => k);

  const topo: string[] = [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    topo.push(id);
    for (const succId of adj.get(id) || []) {
      const deg = (inDegree.get(succId) || 1) - 1;
      inDegree.set(succId, deg);
      if (deg === 0) queue.push(succId);
    }
  }

  const isLinkMutationTrigger =
    triggerSource === 'updateLink' ||
    triggerSource === 'createLink' ||
    triggerSource === 'removeLink';

  if (isLinkMutationTrigger && triggerItemIds.length > 0) {
    for (const triggerId of triggerItemIds) {
      const baseline = baselineSnapshot.get(triggerId);
      const item = itemMap.get(triggerId);
      if (baseline && item && !fixedIds.has(triggerId)) {
        item.start_date = baseline.start_date;
        item.end_date = baseline.end_date;
      }
    }
  }

  // ── Forward pass ──
  const processed = new Set<string>();

  for (const id of topo) {
    if (processed.has(id)) continue;
    processed.add(id);

    const item = itemMap.get(id);
    if (!item || !item.is_included) continue;

    // First ensure the item's own end_date is consistent with start + duration
    if (item.start_date && item.duration_days > 0 && !item.end_date) {
      item.end_date = addWorkingDays(item.start_date, item.duration_days - 1, settings, holidays);
    }

    // If this item is NOT in the downstream set, we still process it so that
    // its dates are consistent (for successors), but we do not record it as
    // "updated" because it was not affected by the trigger.
    const isDownstream = downstreamIds.has(id);

    // Manually fixed items keep their dates — do not re-apply incoming constraints.
    if (fixedIds.has(id)) {
      continue;
    }

    // Gather constraints from ALL incoming links and pick the latest one
    const incoming = links.filter((l) => l.successor_item_id === id);
    let maxConstrainedStart: string | null = null;
    let maxConstrainedEnd: string | null = null;
    let controllingLink: ScheduleLink | null = null;

    for (const link of incoming) {
      const pred = itemMap.get(link.predecessor_item_id);
      if (!pred || !pred.is_included) continue;
      const { constrainedStart, constrainedEnd } = computeLinkConstraint(pred, item, link);
      if (constrainedStart) {
        if (!maxConstrainedStart || parseISODate(constrainedStart) > parseISODate(maxConstrainedStart)) {
          maxConstrainedStart = constrainedStart;
          maxConstrainedEnd = constrainedEnd;
          controllingLink = link;
        }
      }
    }

    if (!maxConstrainedStart) continue;

    const oldStart = item.start_date;
    const oldEnd = item.end_date;

    // Determine if this item is "completed" (has actual dates or marked complete)
    const isCompleted = item.is_complete || !!(item.actual_start_date && item.actual_start_date.trim()) || !!(item.actual_end_date && item.actual_end_date.trim());

    if (isCompleted && skipCompletedItems) {
      // Record that this completed item would have moved, but keep it fixed
      if (maxConstrainedStart !== oldStart || maxConstrainedEnd !== oldEnd) {
        affectedCompleted.push(id);
      }
      // Do NOT change the item — its current dates anchor successors
      continue;
    }

    // Also respect manual_date_lock
    if (item.manual_date_lock) continue;

    // Apply the constrained dates ONLY if this item is in the downstream set.
    // Non-downstream items keep their original dates (they weren't affected
    // by the trigger change) but their dates are still consistent because
    // their predecessors haven't moved.
    const newStart = maxConstrainedStart;
    const newEnd = maxConstrainedEnd || addWorkingDays(newStart, Math.max(1, item.duration_days) - 1, settings, holidays);

    if (isDownstream && (newStart !== oldStart || newEnd !== oldEnd)) {
      if (sourceDeltaDays !== undefined) {
        const baseline = baselineSnapshot.get(id);
        if (baseline?.start_date && newStart) {
          const moveAmount = Math.abs(calendarDayDelta(baseline.start_date, newStart));
          const lag = Math.abs(controllingLink?.lag_days ?? 0);
          const allowed = Math.abs(sourceDeltaDays) + lag + maxExcessMoveDays;
          if (moveAmount > allowed) {
            blockedMoves.push(
              `${item.title}: blocked cascade of ${moveAmount}d (allowed ~${allowed}d from source move)`
            );
            jphDebugLog('Blocked excessive cascade move', {
              itemId: id,
              title: item.title,
              moveAmount,
              allowed,
              sourceDeltaDays,
              lag,
            });
            continue;
          }
        }
      }

      item.start_date = newStart;
      item.end_date = newEnd;

      updated.set(id, {
        start_date: newStart,
        end_date: newEnd,
        duration_days: item.duration_days,
      });

      const predItem = controllingLink ? itemMap.get(controllingLink.predecessor_item_id) : null;
      const reason = controllingLink
        ? `${controllingLink.link_type} + ${controllingLink.lag_days}d from "${predItem?.title || 'unknown'}"`
        : 'manual trigger';

      changeDescriptions.push(
        `${item.title}: ${oldStart || '—'} → ${newStart}${oldEnd ? ` (end ${oldEnd} → ${newEnd})` : ''}`
      );

      jphDebugLog('Recalculated item', {
        itemId: id,
        title: item.title,
        oldStart,
        oldEnd,
        newStart,
        newEnd,
        durationDays: item.duration_days,
        reason,
        controllingDependency: controllingLink
          ? {
              linkType: controllingLink.link_type,
              predecessorId: controllingLink.predecessor_item_id,
              predecessorTitle: predItem?.title || 'unknown',
              lagDays: controllingLink.lag_days,
            }
          : null,
      });
    }
  }

  const result = {
    updatedItems: updated,
    affectedCompletedItems: affectedCompleted,
    circularWarning: null,
    totalMoved: updated.size,
    changeDescriptions,
    blockedMoves,
  };

  jphDebugLog('Schedule recalculation result', {
    projectId: projectId || 'unknown',
    triggerSource: triggerSource || 'auto',
    updatedCount: updated.size,
    affectedCompletedCount: affectedCompleted.length,
    sampleChanges: changeDescriptions.slice(0, 5),
    hasMoreChanges: changeDescriptions.length > 5,
    errors: [],
  });

  return result;
}

// ── Settings display helpers ─────────────────────────────────
/** Build a clear "Working days: Mon–Sat" label */
export function getWorkingDaysSummary(settings: ScheduleSettings): string {
  const days = settings.working_days || [];
  if (days.length === 7) return 'Working days: Mon–Sun';
  if (days.length === 0) return 'Working days: None';

  const has = (d: number) => days.includes(d);
  const start = has(1) ? 'Mon' : has(2) ? 'Tue' : has(3) ? 'Wed' : has(4) ? 'Thu' : has(5) ? 'Fri' : has(6) ? 'Sat' : 'Sun';
  const end = has(6) ? 'Sat' : has(5) ? 'Fri' : has(4) ? 'Thu' : has(3) ? 'Wed' : has(2) ? 'Tue' : has(1) ? 'Mon' : 'Sun';
  if (start === end) return `Working days: ${start}`;
  return `Working days: ${start}–${end}`;
}

/** "Mon-Sat" from settings */
export function getWorkingDaysShort(settings: ScheduleSettings): string {
  const days = settings.working_days || [];
  if (days.length === 7) return 'Mon–Sun';
  if (days.length === 0) return 'None';
  const has = (d: number) => days.includes(d);
  const start = has(1) ? 'Mon' : has(2) ? 'Tue' : has(3) ? 'Wed' : has(4) ? 'Thu' : has(5) ? 'Fri' : has(6) ? 'Sat' : 'Sun';
  const end = has(6) ? 'Sat' : has(5) ? 'Fri' : has(4) ? 'Thu' : has(3) ? 'Wed' : has(2) ? 'Tue' : has(1) ? 'Mon' : 'Sun';
  if (start === end) return start;
  return `${start}–${end}`;
}

export function getExcludedSummary(settings: ScheduleSettings, holidaysCount: number): string {
  const excludedDays: string[] = [];
  if (!settings.working_days.includes(0)) excludedDays.push('Sundays');
  if (!settings.working_days.includes(6)) excludedDays.push('Saturdays');
  let base = excludedDays.length > 0 ? excludedDays.join(' and ') : 'No weekly exclusions';
  if (settings.exclude_public_holidays) {
    base += base ? ' and ' : '';
    base += `${settings.holiday_country || 'NZ'} public holidays`;
  }
  return `Excludes: ${base}`;
}

export function getHolidaySummary(country: string, holidaysCount: number, excludes: boolean): string {
  if (!excludes) return 'Public holidays: included';
  if (holidaysCount > 0) return `${holidaysCount} holidays loaded (${country})`;
  return `No holidays loaded (${country})`;
}

// ── Date formatting helpers (DD/MM/YYYY for schedule/Gantt UI) ──
export function formatDateLabel(iso: string | null): string {
  if (!iso) return '—';
  const d = parseISODate(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${d.getFullYear()}`;
}

export function formatDateFull(iso: string | null): string {
  return formatDateLabel(iso);
}

/** Format ISO date for schedule date text inputs (DD/MM/YYYY). */
export function isoToScheduleDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = parseISODate(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${d.getFullYear()}`;
}

/** Parse DD/MM/YYYY (or ISO YYYY-MM-DD) to ISO date string for storage. Returns null if invalid. */
export function parseScheduleDateInput(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const day = parseInt(slashMatch[1], 10);
    const month = parseInt(slashMatch[2], 10);
    const year = parseInt(slashMatch[3], 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const d = new Date(year, month - 1, day);
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
    return toISODate(d);
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

  return null;
}

export function getDayName(iso: string | null): string {
  if (!iso) return '';
  const d = parseISODate(iso);
  return d.toLocaleDateString('en-NZ', { weekday: 'long' });
}

export function getDayShort(iso: string | null): string {
  if (!iso) return '';
  const d = parseISODate(iso);
  return d.toLocaleDateString('en-NZ', { weekday: 'short' });
}

/** Fields that may carry a schedule task label (display-only resolution). */
export type ScheduleTitleSource = Pick<
  ScheduleItem,
  'title' | 'task_no' | 'sort_order' | 'phase' | 'main_stage' | 'construction_sub_stage' | 'notes'
> & {
  task_name?: string | null;
  name?: string | null;
  stage_name?: string | null;
  description?: string | null;
  display_order?: number | null;
  order_index?: number | null;
};

function pickFirstNonEmptyLabel(...values: (string | null | undefined)[]): string {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return '';
}

/** Stable display title for Gantt rows (handles empty or alternate title fields). */
export function getTaskDisplayTitle(item: ScheduleTitleSource): string {
  const fromFields = pickFirstNonEmptyLabel(
    item.title,
    item.task_name,
    item.name,
    item.stage_name,
    item.main_stage,
    item.construction_sub_stage,
    item.phase,
    item.description,
    item.notes
  );
  if (fromFields) return fromFields;

  if (item.task_no != null && item.task_no > 0) return `Task ${item.task_no}`;

  const orderFallback = item.display_order ?? item.order_index ?? item.sort_order;
  if (orderFallback != null && orderFallback > 0) return `Task ${orderFallback}`;

  return 'Untitled task';
}

type ScheduleSortSource = ScheduleItem & {
  display_order?: number | null;
  order_index?: number | null;
};

function scheduleDisplayOrder(item: ScheduleSortSource): number {
  return item.display_order ?? item.order_index ?? item.sort_order ?? 0;
}

/** Sort schedule items by start date, end date, display order, then created_at. Items without dates go last. */
export function compareScheduleItemsByDate(a: ScheduleSortSource, b: ScheduleSortSource): number {
  const aStart = a.start_date || '';
  const bStart = b.start_date || '';
  if (aStart && bStart) {
    const byStart = aStart.localeCompare(bStart);
    if (byStart !== 0) return byStart;
  } else if (aStart && !bStart) {
    return -1;
  } else if (!aStart && bStart) {
    return 1;
  }

  const aEnd = a.end_date || '';
  const bEnd = b.end_date || '';
  if (aEnd !== bEnd) return aEnd.localeCompare(bEnd);

  const orderDiff = scheduleDisplayOrder(a) - scheduleDisplayOrder(b);
  if (orderDiff !== 0) return orderDiff;

  const aCreated = a.created_at || '';
  const bCreated = b.created_at || '';
  if (aCreated !== bCreated) return aCreated.localeCompare(bCreated);

  const aNo = a.task_no ?? 0;
  const bNo = b.task_no ?? 0;
  return aNo - bNo;
}

export function sortScheduleItemsByDate(items: ScheduleItem[]): ScheduleItem[] {
  return [...items].sort(compareScheduleItemsByDate);
}

export function getPhaseColor(phase: string | null): string {
  if (!phase) return '#9CA3AF';
  /** Palette aligned with 5 Methuen Road Buildertrend reference chart */
  const palette: Record<string, string> = {
    'Pre-Construction': '#6B7280',
    'Site Work': '#F59E0B',
    'Foundations': '#D97706',
    'Framing': '#E8621A',
    'Cladding': '#EC4899',
    'Roofing': '#EF4444',
    'Windows & Doors': '#0891B2',
    'Interior': '#14B8A6',
    'Kitchen': '#059669',
    'Bathroom': '#059669',
    'Fit-out': '#059669',
    'Exterior': '#10B981',
    'Landscaping': '#22C55E',
    'Handover': '#6B7280',
    'Warranty': '#64748B',
  };
  const key = Object.keys(palette).find((k) => phase.toLowerCase().includes(k.toLowerCase()));
  return key ? palette[key] : '#9CA3AF';
}

function normalizeGanttTitleKey(title: string): string {
  return title.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Title and task_no → color maps from 5 Methuen reference schedule (global for all projects). */
const METHUEN_TITLE_COLOR_MAP = new Map<string, string>();
const METHUEN_TASK_NO_COLOR_MAP = new Map<number, string>();
for (const row of buildertrend5MethuenSchedule) {
  METHUEN_TITLE_COLOR_MAP.set(normalizeGanttTitleKey(row.title), row.color);
  METHUEN_TASK_NO_COLOR_MAP.set(row.rowNo, row.color);
}

function resolveMethuenReferenceColor(item: ScheduleItem): string | null {
  const normalizedTitle = normalizeGanttTitleKey(item.title || '');
  if (normalizedTitle) {
    const exact = METHUEN_TITLE_COLOR_MAP.get(normalizedTitle);
    if (exact) return exact;
    for (const [key, color] of METHUEN_TITLE_COLOR_MAP) {
      if (normalizedTitle.includes(key) || key.includes(normalizedTitle)) {
        return color;
      }
    }
  }
  if (item.task_no != null) {
    const byNo = METHUEN_TASK_NO_COLOR_MAP.get(item.task_no);
    if (byNo) return byNo;
  }
  const sortKey = item.sort_order ?? item.display_order ?? item.order_index;
  if (sortKey != null && sortKey > 0) {
    const bySort = METHUEN_TASK_NO_COLOR_MAP.get(sortKey);
    if (bySort) return bySort;
  }
  return null;
}

export function getStageColor(mainStage: string | null, subStage: string | null): string {
  if (subStage) {
    const subPalette: Record<string, string> = {
      'Base': '#D97706',
      'Frame': '#E8621A',
      'Truss': '#EF4444',
      'Roof': '#7C3AED',
      'Exterior': '#0891B2',
      'Plumbing': '#14B8A6',
      'Electrical': '#F59E0B',
      'Insulation': '#6366F1',
      'Interior': '#10B981',
      'Finishing': '#059669',
      'Handover': '#0EA5E9',
      'Warranty': '#64748B',
    };
    const key = Object.keys(subPalette).find((k) => subStage.toLowerCase().includes(k.toLowerCase()));
    if (key) return subPalette[key];
  }
  if (mainStage) {
    const mainPalette: Record<string, string> = {
      'Planning': '#9CA3AF',
      'Design': '#A78BFA',
      'Council': '#F59E0B',
      'Construction': '#E8621A',
      'Interior': '#10B981',
      'Exterior': '#0891B2',
      'Final': '#0EA5E9',
      'Handover': '#059669',
      'Warranty': '#64748B',
    };
    const key = Object.keys(mainPalette).find((k) => mainStage.toLowerCase().includes(k.toLowerCase()));
    if (key) return mainPalette[key];
  }
  return getPhaseColor(null);
}

export function getBarColor(item: ScheduleItem): string {
  // Explicit manual override from color picker / copy-colors tool.
  if (item.bar_color && item.bar_color_mode === 'custom') return item.bar_color;

  // Global 5 Methuen reference mapping by title / task number / sort order.
  const methuenColor = resolveMethuenReferenceColor(item);
  if (methuenColor) return methuenColor;

  if (item.gantt_color) return item.gantt_color;

  if (item.phase) {
    const phaseColor = getPhaseColor(item.phase);
    if (phaseColor !== '#9CA3AF') return phaseColor;
  }

  if (item.construction_sub_stage || item.main_stage) {
    return getStageColor(item.main_stage, item.construction_sub_stage);
  }

  return getPhaseColor(item.phase);
}

export function getContrastColor(hexColor: string): string {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1A1A1A' : '#FFFFFF';
}

export function getStatusColor(progress: number, isComplete: boolean): { bg: string; text: string } {
  if (isComplete || progress >= 100) return { bg: '#F0FDF4', text: '#059669' };
  if (progress >= 75) return { bg: '#ECFDF5', text: '#10B981' };
  if (progress >= 50) return { bg: '#FEF0E7', text: '#E8621A' };
  if (progress > 0) return { bg: '#FFF7ED', text: '#F97316' };
  return { bg: '#F9FAFB', text: '#9CA3AF' };
}

// ── Critical Path Algorithm ─────────────────────────────────

export interface CriticalPathResult {
  criticalItemIds: Set<string>;
  projectEndDate: string | null;
  cycleWarning: string | null;
}

/**
 * Detect cycles in the schedule link graph using DFS.
 */
function detectCycle(
  itemIds: string[],
  links: ScheduleLink[]
): boolean {
  const adj = new Map<string, string[]>();
  for (const id of itemIds) adj.set(id, []);
  for (const l of links) {
    if (adj.has(l.predecessor_item_id) && adj.has(l.successor_item_id)) {
      adj.get(l.predecessor_item_id)!.push(l.successor_item_id);
    }
  }
  const visited = new Set<string>();
  const stack = new Set<string>();

  function dfs(node: string): boolean {
    if (stack.has(node)) return true;
    if (visited.has(node)) return false;
    visited.add(node);
    stack.add(node);
    for (const succ of adj.get(node) || []) {
      if (dfs(succ)) return true;
    }
    stack.delete(node);
    return false;
  }

  for (const id of itemIds) {
    if (dfs(id)) return true;
  }
  return false;
}

/**
 * Compute the critical path over included items.
 * Only FS and SS links are fully supported for critical-path depth.
 * FF / SF are handled via end-date propagation but not path length.
 */
export function calculateCriticalPath(
  items: ScheduleItem[],
  links: ScheduleLink[],
  settings: ScheduleSettings,
  holidays: HolidayInfo[] = []
): CriticalPathResult {
  const included = items.filter((i) => i.is_included);
  const includedIds = included.map((i) => i.id);

  if (included.length === 0) {
    return { criticalItemIds: new Set(), projectEndDate: null, cycleWarning: null };
  }

  if (detectCycle(includedIds, links)) {
    return {
      criticalItemIds: new Set(),
      projectEndDate: null,
      cycleWarning: 'Critical path cannot be calculated because circular dependencies exist.',
    };
  }

  // Build adjacency lists (predecessor -> successors)
  const successors = new Map<string, string[]>();
  const predecessors = new Map<string, string[]>();
  for (const id of includedIds) {
    successors.set(id, []);
    predecessors.set(id, []);
  }
  for (const l of links) {
    if (successors.has(l.predecessor_item_id) && successors.has(l.successor_item_id)) {
      successors.get(l.predecessor_item_id)!.push(l.successor_item_id);
      predecessors.get(l.successor_item_id)!.push(l.predecessor_item_id);
    }
  }

  // Topological order
  const inDegree = new Map<string, number>();
  for (const id of includedIds) inDegree.set(id, 0);
  for (const l of links) {
    if (inDegree.has(l.successor_item_id) && includedIds.includes(l.successor_item_id)) {
      inDegree.set(l.successor_item_id, (inDegree.get(l.successor_item_id) || 0) + 1);
    }
  }
  const queue = includedIds.filter((id) => (inDegree.get(id) || 0) === 0);
  const topo: string[] = [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    topo.push(id);
    for (const succ of successors.get(id) || []) {
      const deg = (inDegree.get(succ) || 1) - 1;
      inDegree.set(succ, deg);
      if (deg === 0) queue.push(succ);
    }
  }

  // Earliest start / finish using forward pass
  const itemMap = new Map(included.map((i) => [i.id, i]));
  const earliestStart = new Map<string, number>();
  const earliestFinish = new Map<string, number>();

  for (const id of topo) {
    const item = itemMap.get(id)!;
    const itemStart = item.start_date ? parseISODate(item.start_date).getTime() : 0;
    const itemEnd = item.end_date ? parseISODate(item.end_date).getTime() : itemStart;

    let es = itemStart;
    for (const predId of predecessors.get(id) || []) {
      const pred = itemMap.get(predId);
      if (!pred) continue;
      const predEF = earliestFinish.get(predId) || parseISODate(pred.end_date || pred.start_date || '1970-01-01').getTime();
      const link = links.find(
        (l) => l.predecessor_item_id === predId && l.successor_item_id === id
      );
      if (link) {
        if (link.link_type === 'FS') {
          const predEnd = pred.end_date || (pred.start_date
            ? addWorkingDays(pred.start_date, Math.max(1, pred.duration_days) - 1, settings, holidays)
            : '1970-01-01');
          const shifted = calculateSuccessorStartDate(
            'FS',
            pred.start_date || '1970-01-01',
            predEnd,
            link.lag_days,
            settings,
            holidays
          );
          if (!shifted) continue;
          const candidate = parseISODate(shifted).getTime();
          if (candidate > es) es = candidate;
        } else if (link.link_type === 'SS') {
          const shifted = calculateSuccessorStartDate(
            'SS',
            pred.start_date || '1970-01-01',
            pred.end_date || pred.start_date || '1970-01-01',
            link.lag_days,
            settings,
            holidays
          );
          if (!shifted) continue;
          const candidate = parseISODate(shifted).getTime();
          if (candidate > es) es = candidate;
        } else if (link.link_type === 'FF') {
          const candidate = predEF + link.lag_days * 86400000;
          if (candidate > itemEnd) {
            es = candidate - (itemEnd - itemStart);
          }
        } else if (link.link_type === 'SF') {
          const candidate = parseISODate(addLagWorkingDays(pred.start_date || '1970-01-01', link.lag_days, settings, holidays)).getTime();
          if (candidate > es) es = candidate;
        }
      }
    }

    earliestStart.set(id, es);
    const dur = item.duration_days || 1;
    const ef = addWorkingDays(toISODate(new Date(es)), dur - 1, settings, holidays);
    earliestFinish.set(id, parseISODate(ef).getTime());
  }

  // Project completion = max earliest finish
  let maxEF = 0;
  let projectEndDate: string | null = null;
  for (const [, ef] of earliestFinish) {
    if (ef > maxEF) maxEF = ef;
  }
  if (maxEF > 0) {
    projectEndDate = toISODate(new Date(maxEF));
  }

  // Backward pass to compute latest start / finish and find slack = 0 items
  const latestFinish = new Map<string, number>();
  const latestStart = new Map<string, number>();

  const terminalIds = includedIds.filter((id) => (successors.get(id) || []).length === 0);
  for (const id of terminalIds) {
    latestFinish.set(id, maxEF);
  }

  for (let i = topo.length - 1; i >= 0; i--) {
    const id = topo[i];
    const item = itemMap.get(id)!;
    const ef = earliestFinish.get(id) || 0;
    let lf = latestFinish.get(id);
    if (lf === undefined) {
      lf = ef;
      latestFinish.set(id, lf);
    }
    const dur = item.duration_days || 1;
    const ls = subtractWorkingDays(toISODate(new Date(lf)), dur - 1, settings, holidays);
    latestStart.set(id, parseISODate(ls).getTime());

    for (const predId of predecessors.get(id) || []) {
      const predLF = latestFinish.get(predId);
      const link = links.find((l) => l.predecessor_item_id === predId && l.successor_item_id === id);
      if (!link) continue;

      let candidateLF: number;
      if (link.link_type === 'FS') {
        const succLS = latestStart.get(id) || 0;
        const succDate = toISODate(new Date(succLS));
        const fsBase = addLagWorkingDays(succDate, -link.lag_days, settings, holidays);
        const predEndCandidate = previousWorkingDay(fsBase, settings, holidays);
        candidateLF = parseISODate(predEndCandidate).getTime();
      } else if (link.link_type === 'SS') {
        const succLS = latestStart.get(id) || 0;
        const succDate = toISODate(new Date(succLS));
        const shiftedBack = subtractWorkingDays(succDate, link.lag_days, settings, holidays);
        candidateLF = parseISODate(shiftedBack).getTime() + (ef - parseISODate(item.start_date || toISODate(new Date(0))).getTime());
      } else if (link.link_type === 'FF') {
        const succLF = latestFinish.get(id) || 0;
        const succDate = toISODate(new Date(succLF));
        const shiftedBack = subtractWorkingDays(succDate, link.lag_days, settings, holidays);
        candidateLF = parseISODate(shiftedBack).getTime();
      } else {
        const succLF = latestFinish.get(id) || 0;
        const succDate = toISODate(new Date(succLF));
        const shiftedBack = subtractWorkingDays(succDate, link.lag_days, settings, holidays);
        candidateLF = parseISODate(shiftedBack).getTime();
      }

      if (predLF === undefined || candidateLF < predLF) {
        latestFinish.set(predId, candidateLF);
      }
    }
  }

  // Critical = slack <= 0 (within 1 day tolerance for working-day rounding)
  const criticalItemIds = new Set<string>();
  for (const id of includedIds) {
    const es = earliestStart.get(id) || 0;
    const ls = latestStart.get(id) || 0;
    const ef = earliestFinish.get(id) || 0;
    const lf = latestFinish.get(id) || 0;
    const slack = Math.abs(ls - es) + Math.abs(lf - ef);
    if (slack < 86400000) {
      criticalItemIds.add(id);
    }
  }

  // Fallback: if no critical items found via CPM (all dates identical, etc),
  // mark the longest chain to the latest end date
  if (criticalItemIds.size === 0 && projectEndDate) {
    const latestItems = included.filter((i) => i.end_date === projectEndDate || i.start_date === projectEndDate);
    const queueBack = latestItems.map((i) => i.id);
    const visitedBack = new Set<string>();
    while (queueBack.length) {
      const id = queueBack.shift()!;
      if (visitedBack.has(id)) continue;
      visitedBack.add(id);
      criticalItemIds.add(id);
      for (const predId of predecessors.get(id) || []) {
        queueBack.push(predId);
      }
    }
  }

  return {
    criticalItemIds,
    projectEndDate,
    cycleWarning: null,
  };
}

// ── Delay / Risk Computation ──────────────────────────────

export interface ScheduleHealthSummary {
  totalItems: number;
  completedItems: number;
  onTrackItems: number;
  atRiskItems: number;
  delayedItems: number;
  criticalItems: number;
  totalDelayDays: number;
  maxDelayDays: number;
  completionImpactDays: number;
  overallHealth: 'good' | 'fair' | 'poor' | 'critical';
}

/**
 * Compute delay_status and schedule_risk_level for each item.
 */
export function computeDelayAndRisk(
  items: ScheduleItem[],
  settings: ScheduleSettings,
  holidays: HolidayInfo[] = []
): Map<string, { delay_status: ScheduleItem['delay_status']; schedule_risk_level: ScheduleItem['schedule_risk_level']; delayDays: number }> {
  const result = new Map<string, { delay_status: ScheduleItem['delay_status']; schedule_risk_level: ScheduleItem['schedule_risk_level']; delayDays: number }>();
  const today = toISODate(new Date());

  for (const item of items) {
    if (!item.is_included) {
      result.set(item.id, { delay_status: 'on_track', schedule_risk_level: 'normal', delayDays: 0 });
      continue;
    }

    let delayDays = 0;
    let status: ScheduleItem['delay_status'] = 'on_track';
    let risk: ScheduleItem['schedule_risk_level'] = 'normal';

    const isComplete = item.is_complete || item.progress >= 100;

    if (isComplete) {
      if (item.actual_end_date && item.baseline_end_date) {
        const actual = parseISODate(item.actual_end_date);
        const base = parseISODate(item.baseline_end_date);
        if (actual > base) {
          delayDays = Math.ceil((actual.getTime() - base.getTime()) / (1000 * 60 * 60 * 24));
          status = 'completed_late';
          risk = 'watch';
        } else {
          status = 'completed_on_time';
        }
      } else {
        status = 'completed_on_time';
      }
    } else {
      const end = item.end_date ? parseISODate(item.end_date) : null;
      const todayD = parseISODate(today);

      if (end && todayD > end) {
        const diff = Math.ceil((todayD.getTime() - end.getTime()) / (1000 * 60 * 60 * 24));
        delayDays = diff;
        status = 'delayed';
        risk = item.is_critical ? 'critical' : 'high';
      } else if (end && item.end_date) {
        const daysUntilEnd = countWorkingDaysInclusive(today, item.end_date, settings, holidays);
        if (daysUntilEnd <= 3 && item.progress < 80) {
          status = 'at_risk';
          risk = item.is_critical ? 'high' : 'watch';
        }
      }
    }

    // Baseline variance for all items
    if (item.baseline_end_date && item.end_date && status !== 'completed_on_time') {
      const cur = parseISODate(item.end_date);
      const base = parseISODate(item.baseline_end_date);
      if (cur > base) {
        const diff = Math.ceil((cur.getTime() - base.getTime()) / (1000 * 60 * 60 * 24));
        if (diff > delayDays) delayDays = diff;
      }
    }

    // Override with manual values if already set and worse than computed
    if (item.delay_status && item.delay_status !== 'on_track' && status === 'on_track') {
      status = item.delay_status;
    }
    if (item.schedule_risk_level && item.schedule_risk_level !== 'normal' && risk === 'normal') {
      risk = item.schedule_risk_level;
    }

    result.set(item.id, { delay_status: status, schedule_risk_level: risk, delayDays });
  }

  return result;
}

/**
 * Compute working-day difference between two dates.
 * Positive = second is later. Negative = second is earlier.
 */
export function workingDayDiff(
  start: string,
  end: string,
  settings: ScheduleSettings,
  holidays: HolidayInfo[] = []
): number {
  const s = parseISODate(start);
  const e = parseISODate(end);
  if (e < s) return -workingDayDiff(end, start, settings, holidays);
  let count = 0;
  const d = new Date(s);
  const endIso = toISODate(e);
  while (true) {
    const iso = toISODate(d);
    if (isWorkingDay(iso, settings, holidays)) count++;
    if (iso === endIso) break;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

export function buildHealthSummary(
  items: ScheduleItem[],
  computed: Map<string, { delay_status: ScheduleItem['delay_status']; schedule_risk_level: ScheduleItem['schedule_risk_level']; delayDays: number }>,
  criticalIds: Set<string>
): ScheduleHealthSummary {
  const included = items.filter((i) => i.is_included);
  const completed = included.filter((i) => i.is_complete);

  let onTrack = 0;
  let atRisk = 0;
  let delayed = 0;
  let totalDelay = 0;
  let maxDelay = 0;

  for (const item of included) {
    const state = computed.get(item.id);
    if (!state) continue;
    if (state.delay_status === 'on_track' || state.delay_status === 'completed_on_time') onTrack++;
    else if (state.delay_status === 'at_risk') atRisk++;
    else if (state.delay_status === 'delayed' || state.delay_status === 'completed_late') delayed++;
    totalDelay += state.delayDays;
    if (state.delayDays > maxDelay) maxDelay = state.delayDays;
  }

  // Completion impact
  const currentEndDates = included
    .filter((i) => i.end_date)
    .map((i) => parseISODate(i.end_date!).getTime());
  const baselineEndDates = included
    .filter((i) => i.baseline_end_date)
    .map((i) => parseISODate(i.baseline_end_date!).getTime());
  const currentMax = currentEndDates.length ? Math.max(...currentEndDates) : 0;
  const baselineMax = baselineEndDates.length ? Math.max(...baselineEndDates) : currentMax;
  const completionImpactDays = Math.max(0, Math.ceil((currentMax - baselineMax) / (1000 * 60 * 60 * 24)));

  // Health rules:
  // Critical if any critical path item is delayed
  // At Risk if any critical path item is at_risk OR more than 3 items are delayed
  // Watch if any item is at_risk OR delayed
  // Healthy if no delayed or at-risk items
  const criticalDelayed = included.some((i) => criticalIds.has(i.id) && computed.get(i.id)?.delay_status === 'delayed');
  const criticalAtRisk = included.some((i) => criticalIds.has(i.id) && computed.get(i.id)?.delay_status === 'at_risk');

  let overallHealth: ScheduleHealthSummary['overallHealth'] = 'good';
  if (criticalDelayed) {
    overallHealth = 'critical';
  } else if (criticalAtRisk || delayed > 3) {
    overallHealth = 'poor';
  } else if (delayed > 0 || atRisk > 0) {
    overallHealth = 'fair';
  }

  return {
    totalItems: included.length,
    completedItems: completed.length,
    onTrackItems: onTrack,
    atRiskItems: atRisk,
    delayedItems: delayed,
    criticalItems: criticalIds.size,
    totalDelayDays: totalDelay,
    maxDelayDays: maxDelay,
    completionImpactDays,
    overallHealth,
  };
}

export function getRiskLevelColor(level: ScheduleItem['schedule_risk_level']): string {
  switch (level) {
    case 'normal': return '#10B981';
    case 'watch': return '#F59E0B';
    case 'high': return '#EF4444';
    case 'critical': return '#7C2D12';
    default: return '#9CA3AF';
  }
}

export function getRiskLevelBg(level: ScheduleItem['schedule_risk_level']): string {
  switch (level) {
    case 'normal': return '#F0FDF4';
    case 'watch': return '#FEF0E7';
    case 'high': return '#FEF2F2';
    case 'critical': return '#FEF2F2';
    default: return '#F9FAFB';
  }
}

export function getDelayStatusLabel(status: ScheduleItem['delay_status']): string {
  switch (status) {
    case 'on_track': return 'On Track';
    case 'at_risk': return 'At Risk';
    case 'delayed': return 'Delayed';
    case 'completed_late': return 'Completed Late';
    case 'completed_on_time': return 'Completed On Time';
    default: return status;
  }
}

export function getDelayStatusColor(status: ScheduleItem['delay_status']): { bg: string; text: string } {
  switch (status) {
    case 'on_track': return { bg: '#F0FDF4', text: '#059669' };
    case 'at_risk': return { bg: '#FEF0E7', text: '#92400E' };
    case 'delayed': return { bg: '#FEF2F2', text: '#EF4444' };
    case 'completed_late': return { bg: '#FEF2F2', text: '#EF4444' };
    case 'completed_on_time': return { bg: '#F0FDF4', text: '#059669' };
    default: return { bg: '#F9FAFB', text: '#9CA3AF' };
  }
}

export function getHealthSummaryColor(health: ScheduleHealthSummary['overallHealth']): { bg: string; text: string } {
  switch (health) {
    case 'good': return { bg: '#F0FDF4', text: '#059669' };
    case 'fair': return { bg: '#FEF0E7', text: '#92400E' };
    case 'poor': return { bg: '#FEF2F2', text: '#EF4444' };
    case 'critical': return { bg: '#FEF2F2', text: '#7C2D12' };
  }
}

export function getHealthSummaryLabel(health: ScheduleHealthSummary['overallHealth']): string {
  switch (health) {
    case 'good': return 'Healthy';
    case 'fair': return 'Watch';
    case 'poor': return 'At Risk';
    case 'critical': return 'Critical';
  }
}

/**
 * Client-friendly health label: simplified status only.
 */
export function getClientHealthLabel(health: ScheduleHealthSummary['overallHealth']): string {
  switch (health) {
    case 'good': return 'On Track';
    case 'fair': return 'Watch';
    case 'poor': return 'Delayed';
    case 'critical': return 'Delayed — Updated completion estimate';
  }
}

/**
 * Build alert messages for the schedule tab.
 */
export interface ScheduleAlert {
  type: 'critical_delayed' | 'at_risk' | 'project_behind' | 'critical_path_warning';
  title: string;
  message: string;
  itemId?: string;
}

export function buildScheduleAlerts(
  items: ScheduleItem[],
  computed: Map<string, { delay_status: ScheduleItem['delay_status']; schedule_risk_level: ScheduleItem['schedule_risk_level']; delayDays: number }>,
  criticalIds: Set<string>,
  completionImpactDays: number,
  cycleWarning?: string | null
): ScheduleAlert[] {
  const alerts: ScheduleAlert[] = [];
  const included = items.filter((i) => i.is_included);

  if (cycleWarning) {
    alerts.push({
      type: 'critical_path_warning',
      title: 'Circular Dependencies',
      message: cycleWarning,
    });
  }

  for (const item of included) {
    if (!criticalIds.has(item.id)) continue;
    const state = computed.get(item.id);
    if (!state) continue;
    if (state.delay_status === 'delayed') {
      alerts.push({
        type: 'critical_delayed',
        title: 'Critical Path Delay',
        message: `${item.title} is delayed and is on the critical path.`,
        itemId: item.id,
      });
    } else if (state.delay_status === 'at_risk') {
      alerts.push({
        type: 'at_risk',
        title: 'Critical Path At Risk',
        message: `${item.title} is at risk and is on the critical path.`,
        itemId: item.id,
      });
    }
  }

  for (const item of included) {
    if (criticalIds.has(item.id)) continue;
    const state = computed.get(item.id);
    if (!state || state.delay_status !== 'at_risk') continue;
    alerts.push({
      type: 'at_risk',
      title: 'At Risk',
      message: `${item.title} is due within 3 working days and progress is below 80%.`,
      itemId: item.id,
    });
  }

  if (completionImpactDays > 0) {
    alerts.push({
      type: 'project_behind',
      title: 'Project Behind Baseline',
      message: `Project completion is ${completionImpactDays} day${completionImpactDays > 1 ? 's' : ''} behind baseline.`,
    });
  }

  return alerts;
}

/**
 * Future-friendly notification builder.
 */
export function buildScheduleNotificationPayload(
  projectId: string,
  alert: ScheduleAlert,
  recipientId?: string
): Record<string, unknown> {
  return {
    project_id: projectId,
    title: alert.title,
    message: alert.message,
    type: 'schedule_alert',
    sub_type: alert.type,
    item_id: alert.itemId || null,
    recipient_id: recipientId || null,
    read: false,
    created_at: new Date().toISOString(),
  };
}
